import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GatewayWithMachinesViewStateTypes,
  MachinesListCompanyEntry,
} from "@src/types/gateways-with-machines";
import { updateGatewayName } from "@src/cloud-connect/use-cases/update-gateway-name";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import {
  setAllMachinesWithGatewaysListData,
  setConnectedGatewaysWithMachinesListData,
  setSearchMachinesWithGatewaysListData,
} from "@src/reducers/gateways-machines-slice";

export const updateGatewayNameInMachineListData = (
  data: Record<string, MachinesListCompanyEntry>,
  gatewayUuid: string,
  name: string,
): Record<string, MachinesListCompanyEntry> =>
  Object.fromEntries(
    Object.entries(data).map(([companyKey, company]) => [
      companyKey,
      {
        ...company,
        machines: Object.fromEntries(
          Object.entries(company.machines).map(([machineId, machine]) => [
            machineId,
            machine.gateway_id === gatewayUuid
              ? { ...machine, gateway_name: name }
              : machine,
          ]),
        ),
      },
    ]),
  );

export const useEditGatewayNameHook = (
  gateway: GatewayWithMachinesViewStateTypes,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const gateways = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.connectedGatewaysListData,
  );
  const allMachinesListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.allMachinesListData,
  );
  const searchMachinesListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.searchMachinesListData,
  );
  const [showInput, setShowInput] = useState(false);
  const [newGatewayName, setNewGatewayName] = useState(gateway.name);

  const texts = {
    errorInputEmpty: i18n("modules.editGatewayName.errorInputEmpty"),
    errorUpdateFailed: i18n("modules.editGatewayName.errorUpdateFailed"),
    successfullyUpdated: i18n("modules.editGatewayName.successfullyUpdated"),
    save: i18n("modules.editGatewayName.save"),
    edit: i18n("modules.editGatewayName.edit"),
    cancel: i18n("modules.editGatewayName.cancel"),
  };

  useEffect(() => {
    setNewGatewayName(gateway.name);
  }, [gateway.name]);

  const updateNewGatewayName = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setNewGatewayName(event.target.value);
  };

  const saveGatewayName = async (
    event: ChangeEvent<HTMLInputElement> | MouseEvent | KeyboardEvent,
  ) => {
    event.stopPropagation();
    const name = newGatewayName.trim();
    if (!name) {
      errorSnackbar(texts.errorInputEmpty);
      return;
    }

    setShowInput(false);
    const response = await updateGatewayName({
      name,
      gateway_id: gateway.gateway_uuid,
    });

    if (!response?.success) {
      errorSnackbar(texts.errorUpdateFailed, 5000);
      setNewGatewayName(gateway.name);
      return;
    }

    dispatch(
      setConnectedGatewaysWithMachinesListData(
        gateways.map((entry) =>
          entry.gateway_uuid === gateway.gateway_uuid
            ? { ...entry, name }
            : entry,
        ),
      ),
    );
    dispatch(
      setAllMachinesWithGatewaysListData(
        updateGatewayNameInMachineListData(
          allMachinesListData,
          gateway.gateway_uuid,
          name,
        ),
      ),
    );
    dispatch(
      setSearchMachinesWithGatewaysListData(
        updateGatewayNameInMachineListData(
          searchMachinesListData,
          gateway.gateway_uuid,
          name,
        ),
      ),
    );
    successSnackbar(texts.successfullyUpdated, 5000);
  };

  const editGatewayName = (event: MouseEvent) => {
    event.stopPropagation();
    setShowInput(true);
  };

  const onKeyDownFunc = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      void saveGatewayName(event);
    }
  };

  const cancelSaveGatewayName = (event: MouseEvent) => {
    event.stopPropagation();
    setNewGatewayName(gateway.name);
    setShowInput(false);
  };

  return {
    showInput,
    newGatewayName,
    texts,
    editGatewayName,
    onKeyDownFunc,
    updateNewGatewayName,
    saveGatewayName,
    cancelSaveGatewayName,
  };
};
